import {
  ArrowUDownLeftIcon,
  NotepadIcon,
  ShoppingCartSimpleIcon,
  TShirtIcon,
  UserCircleIcon,
} from '@phosphor-icons/react'

import { PackageReturnIcon } from '@reveni-io/reveni-ui'

import {
  anyOfMatchTypes,
  displayMatchTypes,
  fullTextMatchTypes,
  idMatchTypes,
  numberMatchTypes,
  numericComparisonMatchTypes,
  numericRangeMatchTypes,
  regionMatchTypes,
} from '@/common/components/MatchTypeEditor/constants'
import { getHierarchicalReasonMenuItems } from '@/common/helpers/helpers'
import {
  filterDisabledProperties,
  getBaseProductProperties,
  getItemRequestTypeProperty,
} from '@/common/helpers/rules/helpers'

const getProductToReturnProperties = ({
  productTypes,
  productCategories,
  productCollections,
  returnReasons,
  language,
  t,
}) => ({
  ...getBaseProductProperties({ productTypes, productCategories, productCollections }),
  return_reason: {
    label: 'Motivo de devolución',
    matchTypes: anyOfMatchTypes,
    hierarchical: true,
    comboItems: getHierarchicalReasonMenuItems(returnReasons, language),
  },
  item_request_type: getItemRequestTypeProperty(t),
})

export const getConditionTypes = ({
  productTypes,
  productCategories,
  productCollections,
  returnReasons,
  regions,
  language,
  disabledConditionFields,
  t,
}) => {
  const conditionTypes = {
    order: {
      label: 'Pedido',
      description: 'Evalúa las propiedades del pedido original.',
      icon: ShoppingCartSimpleIcon,
      scope: 'order',
      properties: {
        order_name: { label: 'Número de pedido', matchTypes: [...numberMatchTypes, displayMatchTypes.valueInRange] },
        order_postal_code: {
          label: 'Código postal del pedido',
          matchTypes: [...numberMatchTypes, displayMatchTypes.valueInRange],
        },
        order_date: {
          label: 'Fecha de pedido',
          matchTypes: [
            displayMatchTypes.olderThan,
            displayMatchTypes.newerThan,
            displayMatchTypes.dateInRange,
            displayMatchTypes.notDateInRange,
          ],
        },
        region: { label: 'Región del pedido', matchTypes: regionMatchTypes, comboItems: regions },
        status: { label: 'Estado del pedido', matchTypes: idMatchTypes },
        order_type: {
          label: 'Tipo de pedido',
          matchTypes: idMatchTypes,
          comboItems: [
            { value: 'initial', label: t('Pedido inicial') },
            { value: 'exchange', label: t('Pedido de cambio') },
          ],
        },
        order_net_value: { label: 'Valor neto del pedido', matchTypes: numericComparisonMatchTypes },
        order_original_value: { label: 'Valor original del pedido', matchTypes: numericComparisonMatchTypes },
        order_tag: { label: 'Etiqueta del pedido', matchTypes: fullTextMatchTypes },
        order_source: { label: 'Origen del pedido', matchTypes: fullTextMatchTypes },
        has_discounts: { label: '¿Tiene descuentos aplicados?', matchTypes: [displayMatchTypes.boolean] },
        discount_applied: { label: 'Descuento aplicado', matchTypes: idMatchTypes },
      },
    },
    customer: {
      label: 'Cliente',
      description: 'Evalúa las propiedades del cliente que realizó el pedido.',
      icon: UserCircleIcon,
      scope: 'customer',
      properties: {
        customer_email: { label: 'Email del cliente', matchTypes: fullTextMatchTypes },
        customer_tag: { label: 'Etiqueta del cliente', matchTypes: fullTextMatchTypes },
        customer_group: { label: 'Grupo del cliente', matchTypes: idMatchTypes },
      },
    },
    request: {
      label: 'Solicitud',
      description: 'Evalúa las propiedades de la solicitud de devolución o cambio.',
      icon: NotepadIcon,
      scope: 'transaction',
      properties: {
        request_type: {
          label: 'Tipo de solicitud',
          matchTypes: idMatchTypes,
          comboItems: [
            { value: 'refund', label: t('Devolución') },
            { value: 'exchange', label: t('Cambio') },
            { value: 'mixed', label: t('Mixta') },
          ],
        },
        has_upsells: { label: '¿Incluye cambios por múltiples productos?', matchTypes: [displayMatchTypes.boolean] },
        refund_total_amount: { label: 'Importe total del reembolso', matchTypes: numericComparisonMatchTypes },
        payment_total_amount: { label: 'Importe total del pago', matchTypes: numericComparisonMatchTypes },
      },
    },
    productInOrder: {
      label: 'Producto en el pedido',
      description:
        'Evalúa las propiedades de los productos del pedido original, incluyendo los productos que no se están devolviendo.',
      icon: TShirtIcon,
      scope: 'item',
      itemContext: 'in_order',
      supportsAppliesWhen: true,
      properties: getBaseProductProperties({ productTypes, productCategories, productCollections }),
    },
    productToReturn: {
      label: 'Producto a devolver',
      description: 'Evalúa las propiedades de los productos seleccionados para devolución o cambio.',
      icon: PackageReturnIcon,
      scope: 'item',
      itemContext: 'to_be_returned',
      supportsAppliesWhen: true,
      properties: getProductToReturnProperties({
        productTypes,
        productCategories,
        productCollections,
        returnReasons,
        language,
        t,
      }),
    },
    return: {
      label: 'Devolución',
      description: 'Condiciones basadas en las propiedades de la solicitud de envío de devolución enviada.',
      icon: ArrowUDownLeftIcon,
      scope: 'return',
      properties: {
        return_weight: { label: 'Peso de la devolución (g)', matchTypes: numericRangeMatchTypes },
        return_number_of_products: {
          label: 'Número de productos incluidos',
          matchTypes: numericComparisonMatchTypes,
        },
      },
    },
  }

  return filterDisabledProperties(conditionTypes, disabledConditionFields)
}
